/**
 * Desktop dev input: WASD / arrow keys move; the mouse aims; left-click or
 * Space fires toward the cursor.
 *
 * Aim is computed by casting a ray from the cursor onto the ground plane
 * (y = 0) and pointing from the character toward that world point.
 */
import { Matrix } from "@babylonjs/core/Maths/math.vector.js";
// Side-effect import: augments Scene with createPickingRay (tree-shaking needs this).
import "@babylonjs/core/Culling/ray.js";
import type { Scene } from "@babylonjs/core/scene.js";
import { vec2, normalize, clampLength, type Vec2 } from "@allstars/shared";
import type { InputSample, InputSource } from "./types.js";

type CharPos = () => { x: number; z: number };

export const createKeyboardMouseInput = (
  scene: Scene,
  getCharWorldPos: CharPos,
): InputSource => {
  const pressed = new Set<string>();
  let mouseX = 0;
  let mouseY = 0;
  let mouseSeen = false;
  let fireRequested = false;

  const onKeyDown = (e: KeyboardEvent): void => {
    const key = e.key.toLowerCase();
    pressed.add(key);
    if (key === " " || e.key === "Spacebar") fireRequested = true;
  };
  const onKeyUp = (e: KeyboardEvent): void => {
    pressed.delete(e.key.toLowerCase());
  };
  const onPointerMove = (e: PointerEvent): void => {
    mouseX = e.clientX;
    mouseY = e.clientY;
    mouseSeen = true;
  };
  const onPointerDown = (e: PointerEvent): void => {
    // Mouse only: never let touch taps (handled by the touch sticks) fire here.
    if (e.pointerType === "mouse" && e.button === 0) fireRequested = true;
  };

  window.addEventListener("keydown", onKeyDown);
  window.addEventListener("keyup", onKeyUp);
  window.addEventListener("pointermove", onPointerMove);
  window.addEventListener("pointerdown", onPointerDown);

  const has = (...keys: string[]): boolean => keys.some((k) => pressed.has(k));

  const readMove = (): Vec2 => {
    const x = (has("d", "arrowright") ? 1 : 0) - (has("a", "arrowleft") ? 1 : 0);
    const y = (has("w", "arrowup") ? 1 : 0) - (has("s", "arrowdown") ? 1 : 0);
    return clampLength({ x, y }, 1);
  };

  const readAim = (): Vec2 => {
    const cam = scene.activeCamera;
    const canvas = scene.getEngine().getRenderingCanvas();
    if (!cam || !canvas || !mouseSeen) return vec2(0, 0);
    const rect = canvas.getBoundingClientRect();
    const ray = scene.createPickingRay(mouseX - rect.left, mouseY - rect.top, Matrix.Identity(), cam);
    if (Math.abs(ray.direction.y) < 1e-6) return vec2(0, 0);
    const t = -ray.origin.y / ray.direction.y;
    const gx = ray.origin.x + ray.direction.x * t;
    const gz = ray.origin.z + ray.direction.z * t;
    const c = getCharWorldPos();
    return normalize({ x: gx - c.x, y: gz - c.z });
  };

  return {
    read: (): InputSample => {
      const fire = fireRequested;
      fireRequested = false;
      return { move: readMove(), aim: readAim(), fire };
    },
    get active(): boolean {
      return has("w", "a", "s", "d", "arrowup", "arrowdown", "arrowleft", "arrowright");
    },
    dispose(): void {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("keyup", onKeyUp);
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerdown", onPointerDown);
    },
  };
};
